<template>
  <div class="sa-detail" v-if="report">
    <!-- Detail navigation and request actions -->
    <div class="sa-detail-toolbar">
      <div v-if="canCancelTicket(report)">
        <button
          type="button"
          class="btn btn-outline-danger cancel-ticket-button font-weight-bold"
          @click="cancelTicket"
          :disabled="isProcessing"
        >
          <CIcon name="cil-ban" size="sm" class="mr-1" />
          {{ $t('ticketDetail.cancelTicketBtn') }}
        </button>
      </div>
      <button type="button" class="btn sa-detail-back" @click="$emit('close')">
        {{ text('กลับหน้ารายการ', 'Back to requests') }}
      </button>
    </div>

    <!-- Hero Banner -->
    <section class="sa-detail-hero">
      <div>
        <p>{{ text('งานแจ้งซ่อม', 'Repair request') }}</p>
        <h1>{{ report.ticketNumber }}</h1>
        <span>{{ getUserName() }} • {{ getReporterAffiliation() }}</span>
      </div>
      <div class="sa-current-status">
        <strong :class="{ 'status-cancelled': report.status === 'cancelled' }">{{ statusLabel(report.status) }}</strong>
        <p>{{ nextStep }}</p>
        <small>{{ text('ข้อมูลล่าสุด', 'Updated') }} {{ formatDate(report.updatedAt || report.createdAt) }}</small>
      </div>
    </section>

    <!-- Panel 1: ความคืบหน้าการซ่อม & ข้อมูลตามขั้นตอน -->
    <section class="sa-detail-panel">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap" style="gap: 10px;">
        <h2 class="m-0">{{ text('ความคืบหน้าการซ่อม', 'Repair progress') }}</h2>
        <div>
          <button
            type="button"
            class="btn btn-sm font-weight-bold shadow-sm d-flex align-items-center"
            style="background-color: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; border-radius: 8px; padding: 6px 14px; transition: all 0.2s ease-in-out;"
            @click="selectStep(activeStep === 'all' ? defaultDetailStep : 'all')"
          >
            <CIcon name="cil-list" size="sm" class="mr-1 text-primary" />
            {{ activeStep === 'all' ? text('ดูแยกตามขั้นตอน', 'View by step') : text('ดูข้อมูลทั้งหมด', 'View all') }}
          </button>
        </div>
      </div>

      <ol class="sa-timeline">
        <li
          v-for="(step, index) in timeline"
          :key="step.status"
          :class="{
            reached: step.reached,
            current: step.status === report.status,
            'active-step': activeStep === step.status,
            'disabled-step': !step.reached
          }"
          :aria-current="step.status === report.status ? 'step' : null"
          v-keyboard-activate role="button" :tabindex="step.reached ? 0 : -1" :aria-disabled="String(!step.reached)" :aria-pressed="String(activeStep === step.status)"
          @click="step.reached ? selectStep(step.status) : null"
          :title="step.reached ? text('คลิกเพื่อดูขั้นตอนนี้', 'Click to view step') : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached yet')"
        >
          <span class="sa-step-number">{{ index + 1 }}</span>
          <strong>{{ step.label }}</strong>
          <small>{{ step.date ? formatDate(step.date) : (step.reached ? (step.status === 'assigned' && report.status === 'pending' ? text('รอดำเนินการ', 'Pending') : text('ไม่มีเวลาบันทึก', 'Time not recorded')) : text('ยังไม่ถึงขั้นตอนนี้', 'Not reached')) }}</small>
        </li>
      </ol>

      <p v-if="report.status === 'cancelled'" class="alert alert-secondary">
        <strong class="text-danger">{{ text('ยกเลิกงาน:', 'Cancelled:') }}</strong> {{ report.cancelReason || text('ไม่ได้ระบุเหตุผล', 'No reason recorded') }}
      </p>

      <!-- Step Content Container -->
      <div class="sa-step-container">
        <!-- Step 1: แจ้งเรื่อง -->
        <div v-show="(activeStep === 'pending' || activeStep === 'all') && isStepReached('pending')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">1</span>
              {{ text('รายละเอียดการแจ้งซ่อม', 'Report details') }}
            </h3>
            <span class="text-muted small">{{ formatDate(getReportInformedDate()) }}</span>
          </div>

          <TicketDetailSummary
            :ticket-number="report.ticketNumber || '-'"
            :reporter="getUserName()"
            :affiliation="getReporterAffiliation()"
            :section="getSectionName()"
            :personal-phone="getPersonalPhone()"
            :office-phone="getOfficePhone()"
            :job-type="getCategoryName()"
            :work-area="getLocationFull()"
            :time-slot="getAvailableTime()"
            :on-behalf-of="getOnBehalfOf()"
            :technician-phone="getTechnicianPhone()"
          />
          <h4 class="mt-4 mb-2 font-weight-bold" style="font-size: 16px; color: #1e293b;">
            {{ text('รายละเอียดปัญหา', 'Problem description') }}
          </h4>
          <p class="sa-description" v-html="getProblemDetails()"></p>
          <p v-if="getAssetCode()" class="font-weight-bold text-danger mb-3">
            <CIcon name="cil-tag" class="mr-1" /> {{ text('รหัสครุภัณฑ์:', 'Asset code:') }} {{ getAssetCode() }}
          </p>

          <div v-if="isOutdoor()" class="mb-4">
            <label class="lbl font-weight-bold text-dark mb-2">{{ $t('ticketDetail.mapPoint') }}</label>
            <div class="map-container rounded overflow-hidden" style="height: 350px;">
              <div ref="mapContainer" style="width: 100%; height: 100%"></div>
            </div>
            <div class="mt-2 text-right">
              <a :href="`https://www.google.com/maps?q=${mapPosition.lat},${mapPosition.lng}`" target="_blank" class="btn btn-sm btn-outline-primary">
                <CIcon name="cil-map" class="mr-1" /> {{ $t('ticketDetail.openMaps') }} ↗
              </a>
            </div>
          </div>

          <div class="mt-3">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพปัญหาที่แจ้ง', 'Reported problem photo') }}</h5>
            <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" @open="openImage" @error="handleImageError" />
            <p v-else class="text-muted small">{{ text('ไม่มีภาพแนบ', 'No image attached') }}</p>
          </div>


        </div>

        <!-- Step 2: รับเรื่อง/มอบหมาย -->
        <div v-show="(activeStep === 'assigned' || activeStep === 'all') && isStepReached('assigned')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">2</span>
              {{ report.status === 'pending' ? text('การมอบหมายช่างผู้รับผิดชอบ', 'Assign technicians') : text('การรับเรื่องและมอบหมายงาน', 'Assignment details') }}
            </h3>
            <span class="text-muted small" v-if="report.supervisorPart && report.supervisorPart.dateTimeInformed">
              {{ formatDate(report.supervisorPart.dateTimeInformed) }}
            </span>
          </div>

          <!-- Postpone Active Notice inside Step 2 -->
          <div v-if="isPostponedActive" class="postpone-status-banner p-3 mb-4 rounded border" style="background: #fffbeb; border-color: #fde68a !important;">
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
              <div class="d-flex align-items-center">
                <span class="mr-2" style="font-size: 1.5rem;">⏸️</span>
                <div>
                  <strong class="text-dark d-block" style="font-size: 1rem;">
                    {{ text('สถานะงาน: เลื่อนซ่อม', 'Repair Postponed') }}
                  </strong>
                  <span class="text-muted small">
                    {{ text('ticket จะวนมาใหม่อีกครั้งในวันที่', 'Ticket will resume on') }}
                    <strong class="text-primary">{{ formatDate(report.postponedUntil) }}</strong>
                    ({{ text('เลื่อนซ่อมครั้งที่', 'Postpone count:') }} {{ postponeCount }}/2)
                    - {{ text('ยังไม่เริ่มนับ SLA จนกว่าจะถึงเวลาดังกล่าว', 'SLA countdown paused until this date') }}
                  </span>
                </div>
              </div>
              <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <button
                  v-if="!isEditingEta"
                  type="button"
                  class="btn btn-sm btn-outline-warning text-dark font-weight-bold"
                  @click="startEditEta"
                >
                  <CIcon name="cil-pencil" size="sm" class="mr-1" />
                  {{ text('แก้ไข ETA / เลื่อนซ่อม', 'Edit ETA / Postpone') }}
                </button>

              </div>
            </div>

            <!-- Editable ETA dropdown while in Step 2 -->
            <div v-if="isEditingEta" class="mt-3 pt-3 border-top d-flex align-items-center flex-wrap" style="gap: 8px;">
              <select class="form-control form-control-sm d-inline-block" style="width: auto; min-width: 200px;" v-model="etaOption">
                <option value="immediate">{{ text('ดำเนินการซ่อมได้เลย', 'Proceed immediately') }}</option>
                <option value="waiting_parts">{{ text('รออะไหล่', 'Waiting for spare parts') }}</option>
                <option value="postponed" :disabled="isPostponeLimitReached">
                  {{ postponeOptionLabel }}
                </option>
              </select>
              <input
                v-if="etaOption === 'waiting_parts'"
                type="text"
                class="form-control form-control-sm d-inline-block"
                style="width: 260px; min-width: 200px;"
                v-model="etaWaitingText"
                :placeholder="text('ระบุรอกี่วัน (เช่น รอปั๊มน้ำ 3 วัน)', 'e.g. 3 days')"
              />
              <CButton
                size="sm"
                color="primary"
                class="font-weight-bold px-3 py-1"
                style="background-color: #1e40af; border-color: #1e40af; border-radius: 6px;"
                @click="saveEta"
                :disabled="(etaOption === 'waiting_parts' && (!etaWaitingText || !etaWaitingText.trim())) || (etaOption === 'postponed' && isPostponeLimitReached) || isProcessing"
              >
                {{ isProcessing ? $t('ticketDetail.saving') : text('บันทึก', 'Save') }}
              </CButton>
              <CButton
                size="sm"
                color="secondary"
                variant="outline"
                class="py-1 px-2"
                style="border-radius: 6px;"
                @click="isEditingEta = false"
              >
                {{ $t('mgmt.cancel') }}
              </CButton>
            </div>
          </div>

          <!-- Assignment Meta Info (3 Columns) - Placed at Top -->
          <div class="assignment-meta-grid mb-4">
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-institution" size="sm" class="mr-1 text-primary" />
                {{ text('หน่วยงานรับผิดชอบ', 'Maintenance section') }}
              </span>
              <strong class="meta-value">{{ getSectionName() }}</strong>
            </div>
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-calendar" size="sm" class="mr-1 text-primary" />
                {{ text('กำหนดซ่อม', 'Repair deadline') }}
              </span>
              <strong class="meta-value" :class="{ 'text-danger': overdue }">
                <template v-if="isPostponedActive">
                  {{ text('เลื่อนซ่อม (เริ่มนับ SLA: ', 'Postponed (SLA starts: ') }}{{ formatDate(report.postponedUntil) }})
                </template>
                <template v-else>
                  {{ report.slaDeadline ? formatDate(report.slaDeadline) : text('ยังไม่ระบุ', 'Not set') }}
                </template>
              </strong>
              <small v-if="overdue" class="badge badge-danger mt-1">{{ text('เกินกำหนดซ่อม', 'Overdue') }}</small>
            </div>
            <div class="meta-item">
              <span class="meta-label">
                <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                {{ text('ระยะเวลาซ่อมโดยประมาณ', 'Estimated repair time') }}
              </span>
              <strong class="meta-value text-primary">{{ getEtaText() || text('ยังไม่ระบุ', 'Not set') }}</strong>
            </div>
          </div>

          <!-- Technicians Team Card (contains assignment form when pending/editing) -->
          <div class="assignment-team-card mb-4">
            <div class="team-card-header d-flex justify-content-between align-items-center flex-wrap pb-2 mb-3 border-bottom">
              <div class="d-flex align-items-center">
                <CIcon name="cil-people" class="mr-2 text-primary" style="font-size: 1.2rem;" />
                <h5 class="mb-0 font-weight-bold text-dark" style="font-size: 15px;">
                  {{ text('ทีมช่างผู้รับผิดชอบ', 'Assigned technicians') }}
                </h5>
                <span v-if="report.supervisorPart && report.supervisorPart.assignments && report.supervisorPart.assignments.length > 0" class="badge badge-pill badge-primary ml-2">
                  {{ report.supervisorPart.assignments.length }} {{ text('ท่าน', 'technicians') }}
                </span>
              </div>
              <div v-if="['assigned', 'in_progress'].includes(report.status) && Boolean(report.userPart && report.userPart.category) && !isInlineAssigning">
                <CButton size="sm" color="outline-primary" class="font-weight-bold" @click="startInlineAssign">
                  <CIcon name="cil-pencil" size="sm" class="mr-1" />
                  {{ text('เปลี่ยนช่าง', 'Change technician') }}
                </CButton>
              </div>
            </div>

            <!-- Inline Assignment Form inside Team Card -->
            <div v-if="report.status === 'pending' || (report.status === 'assigned' && !(report.userPart && report.userPart.category)) || isInlineAssigning" class="p-3 mb-3 rounded border bg-light">
              <h6 class="font-weight-bold text-dark mb-3">
                <CIcon name="cil-user-plus" class="mr-1 text-primary" /> {{ report.status === 'pending' ? text('มอบหมายช่างผู้รับผิดชอบ', 'Assign technicians') : text('แก้ไขการมอบหมายช่าง', 'Change technician assignment') }}
              </h6>

              <div v-if="!showForwardBox">
                <div v-for="(id, index) in selectedTechnicianIds" :key="'tech-' + index" class="mb-2">
                  <div class="small font-weight-bold text-muted mb-1">
                    <span v-if="index === 0" class="badge badge-primary mr-1">{{ $t('ticketDetail.techLeader') }}</span>
                    <span v-else class="badge badge-secondary mr-1">{{ $t('ticketDetail.techHelper') }} {{ index }}</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <vSelect
                      class="assignment-select flex-grow-1"
                      @open="fetchTechnicians"
                      v-model="selectedTechnicianIds[index]"
                      :options="availableTechnicians"
                      :reduce="tech => tech._id"
                      label="name"
                      :placeholder="$t('ticketDetail.placeholderTech')"
                      :clearable="false"
                      :filter-by="filterTechnicianOption"
                    >
                      <template #option="tech">
                        <span class="assignment-option">
                          <span class="assignment-option-name">{{ technicianOptionLabel(tech) }}</span>
                          <span class="assignment-availability" :class="technicianAvailabilityClass(tech)">{{ technicianAvailabilityLabel(tech) }}</span>
                        </span>
                      </template>
                      <template #selected-option="tech">
                        <span class="assignment-option">
                          <span class="assignment-option-name">{{ technicianOptionLabel(tech) }}</span>
                          <span class="assignment-availability" :class="technicianAvailabilityClass(tech)">{{ technicianAvailabilityLabel(tech) }}</span>
                        </span>
                      </template>
                    </vSelect>
                    <button v-if="selectedTechnicianIds.length > 1" type="button" class="btn btn-sm btn-outline-danger ml-2" @click="removeAssignee(index)" :title="$t('ticketDetail.removeAssignee')">
                      ✕
                    </button>
                  </div>
                </div>

                <button v-if="selectedTechnicianIds.length < 3" type="button" class="assignment-secondary btn btn-sm btn-outline-secondary mt-1 mb-3" @click="addAssignee">
                  {{ $t('ticketDetail.addAssignee') }}
                </button>

                <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                  <CButton
                    class="assignment-primary text-white font-weight-bold px-4"
                    @click="assignTask"
                    :disabled="!isValidAssignees || isProcessing"
                  >
                    {{ isProcessing ? $t('ticketDetail.saving') : (!isValidAssignees ? $t('ticketDetail.selectTechFirst') : (report.status === 'pending' ? $t('ticketDetail.assignInline') : $t('ticketDetail.confirmChangeTechBtn'))) }}
                  </CButton>
                  <CButton
                    class="assignment-secondary"
                    v-if="report.status !== 'pending'"
                    color="secondary"
                    @click="cancelInlineAssign"
                    :disabled="isProcessing"
                  >
                    {{ $t('mgmt.cancel') }}
                  </CButton>
                  <CButton
                    class="assignment-forward"
                    v-if="report.status === 'pending' || (report.status === 'assigned' && !(report.userPart && report.userPart.category))"
                    color="outline-secondary"
                    size="sm"
                    @click="showForwardBox = true"
                    :disabled="isProcessing"
                  >
                    <CIcon name="cil-share-alt" class="mr-1" /> {{ $t('ticketDetail.supervisor.forwardOther') }}
                  </CButton>
                </div>
              </div>

              <!-- Forward department box -->
              <div v-if="showForwardBox" class="p-3 mb-2 rounded border" style="background: #f8fafc; border-left: 5px solid #1e40af !important;">
                <h5 class="font-weight-bold text-primary mb-3">
                  <CIcon name="cil-share-alt" class="mr-1" /> {{ $t('ticketDetail.supervisor.forwardOtherShort') }}
                </h5>
                <div class="form-group mb-3">
                  <label class="font-weight-bold text-dark">{{ $t('ticketDetail.supervisor.pickTargetDept') }}</label>
                  <select class="form-control mb-3" v-model="selectedForwardCategoryId">
                    <option :value="null" disabled>{{ $t('ticketDetail.supervisor.placeholderDept') }}</option>
                    <option v-for="cat in allCategories.filter(Boolean)" :key="cat._id" :value="cat._id" :disabled="isCurrentCategory(cat._id)">
                      {{ cat.departmentName }} <span v-if="isCurrentCategory(cat._id)">{{ $t('ticketDetail.supervisor.yourDeptTag') }}</span>
                    </option>
                  </select>
                  <div class="d-flex align-items-center" style="gap: 8px;">
                    <CButton
                      color="primary"
                      class="assignment-forward text-white px-4 font-weight-bold"
                      @click="forwardTicket"
                      :disabled="!selectedForwardCategoryId || isProcessing"
                    >
                      {{ isProcessing ? $t('ticketDetail.supervisor.processing') : $t('ticketDetail.supervisor.confirmForward') }}
                    </CButton>
                    <CButton
                      class="assignment-secondary"
                      color="secondary"
                      @click="showForwardBox = false"
                      :disabled="isProcessing"
                    >
                      {{ $t('mgmt.cancel') }}
                    </CButton>
                  </div>
                </div>
              </div>
            </div>

            <!-- Technicians List / Cards -->
            <div v-if="!isInlineAssigning">
              <div v-if="report.supervisorPart && report.supervisorPart.assignments && report.supervisorPart.assignments.length > 0" class="row tech-cards-row">
                <div
                  v-for="(item, idx) in report.supervisorPart.assignments"
                  :key="idx"
                  class="col-lg-4 col-md-6 col-12 mb-3"
                >
                  <div class="tech-card-item p-3 rounded border h-100 bg-white shadow-sm d-flex flex-column justify-content-between">
                    <div>
                      <div class="d-flex align-items-center mb-2">
                        <div class="tech-avatar mr-2">
                          <CIcon name="cil-user" />
                        </div>
                        <div class="overflow-hidden">
                          <div class="font-weight-bold text-dark text-truncate" :title="getAssigneeItemName(item)">
                            {{ getAssigneeItemName(item) }}
                          </div>
                          <small class="text-muted d-block text-truncate" v-if="item.assignee && item.assignee.jobType && item.assignee.jobType !== '-'">
                            {{ item.assignee.jobType }}
                          </small>
                        </div>
                      </div>
                      <div class="d-flex flex-wrap align-items-center mb-2" style="gap: 4px;">
                        <span class="badge badge-primary" v-if="idx === 0">{{ $t('ticketDetail.techLeader') }}</span>
                        <span class="badge badge-secondary" v-else>{{ $t('ticketDetail.techHelper') }}</span>
                        <span class="badge badge-info">{{ text('สัดส่วน', 'Share') }} {{ item.percentage }}%</span>
                      </div>
                    </div>
                    <div class="pt-2 border-top d-flex align-items-center justify-content-between">
                      <span class="small text-muted">{{ text('สถานะการตอบรับ', 'Status') }}:</span>
                      <span v-if="item.status === 'accepted'" class="badge badge-success px-2 py-1">{{ $t('ticketDetail.tech.badgeAccepted') }}</span>
                      <span v-else-if="item.status === 'rejected'" class="badge badge-danger px-2 py-1">{{ $t('ticketDetail.tech.badgeRejected') }}</span>
                      <span v-else class="badge badge-warning text-dark px-2 py-1">{{ $t('ticketDetail.tech.badgeWaiting') }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="p-3 text-center text-muted bg-white rounded border">
                <strong>{{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}</strong>
                <div v-if="['pending', 'assigned'].includes(report.status)" class="mt-2">
                  <CButton size="sm" color="primary" @click="startInlineAssign">
                    <CIcon name="cil-user-plus" size="sm" class="mr-1" />
                    {{ text('มอบหมายช่าง', 'Assign technician') }}
                  </CButton>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: กำลังซ่อม -->
        <div v-show="(activeStep === 'in_progress' || activeStep === 'all') && isStepReached('in_progress')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">3</span>
              {{ text('การดำเนินงานซ่อม', 'Repair in progress') }}
            </h3>
            <span class="text-muted small" v-if="report.technicianPart && (report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted)">
              {{ formatDate(report.technicianPart.dateTimeStarted || report.technicianPart.dateTimeAccepted) }}
            </span>
          </div>

          <div class="p-3 rounded bg-light border mb-3">
            <div class="row align-items-center">
              <div class="col-md-6 col-12 mb-3 mb-md-0">
                <span class="text-muted small d-block mb-1">{{ text('สถานะการดำเนินงาน', 'Progress status') }}</span>
                <strong class="text-dark" style="font-size: 0.98rem;">{{ report.status === 'in_progress' ? text('ช่างกำลังดำเนินการซ่อมแซม', 'Technician is actively working on the repair') : (['done', 'welldone'].includes(report.status) ? text('ดำเนินการซ่อมเรียบร้อยแล้ว', 'Repair work completed') : text('รอช่างเริ่มดำเนินการ', 'Awaiting repair start')) }}</strong>
              </div>
              <div class="col-md-6 col-12">
                <span class="text-muted small d-block mb-1">{{ text('ระยะเวลาซ่อมโดยประมาณ (ETA)', 'Estimated repair time') }} <span v-if="!getEtaText() || isEditingEta" class="text-danger">*</span></span>

                <div v-if="getEtaText() && !isEditingEta" class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                  <strong class="text-primary mr-2" style="font-size: 1.05rem;">{{ getEtaText() }}</strong>
                  <button
                    v-if="['pending', 'assigned', 'in_progress'].includes(report.status)"
                    type="button"
                    class="btn btn-sm btn-outline-primary py-1 px-3 font-weight-bold shadow-sm"
                    style="border-radius: 6px;"
                    @click="startEditEta"
                    :title="text('แก้ไข ETA', 'Edit ETA')"
                  >
                    <CIcon name="cil-pencil" size="sm" class="mr-1" />
                    {{ text('แก้ไข ETA', 'Edit ETA') }}
                  </button>
                </div>

                <div v-else-if="['pending', 'assigned', 'in_progress'].includes(report.status)" class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                  <select class="form-control form-control-sm d-inline-block" style="width: auto; min-width: 200px;" v-model="etaOption">
                    <option value="immediate">{{ text('ดำเนินการซ่อมได้เลย', 'Proceed immediately') }}</option>
                    <option value="waiting_parts">{{ text('รออะไหล่', 'Waiting for spare parts') }}</option>
                    <option value="postponed" :disabled="isPostponeLimitReached">
                      {{ postponeOptionLabel }}
                    </option>
                  </select>
                  <input
                    v-if="etaOption === 'waiting_parts'"
                    type="text"
                    class="form-control form-control-sm d-inline-block"
                    style="width: 260px; min-width: 200px;"
                    v-model="etaWaitingText"
                    :placeholder="text('ระบุรอกี่วัน (เช่น รอปั๊มน้ำ 3 วัน)', 'e.g. 3 days')"
                  />
                  <CButton
                    size="sm"
                    color="primary"
                    class="font-weight-bold px-3 py-1"
                    style="background-color: #1e40af; border-color: #1e40af; border-radius: 6px;"
                    @click="saveEta"
                    :disabled="(etaOption === 'waiting_parts' && (!etaWaitingText || !etaWaitingText.trim())) || (etaOption === 'postponed' && isPostponeLimitReached) || isProcessing"
                  >
                    {{ isProcessing ? $t('ticketDetail.saving') : text('บันทึก', 'Save') }}
                  </CButton>
                  <CButton
                    v-if="getEtaText()"
                    size="sm"
                    color="secondary"
                    variant="outline"
                    class="py-1 px-2"
                    style="border-radius: 6px;"
                    @click="isEditingEta = false"
                  >
                    {{ $t('mgmt.cancel') }}
                  </CButton>
                </div>
                <div v-else>
                  <strong class="text-muted">{{ text('ยังไม่ระบุ', 'Not set') }}</strong>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Repair Form for Admin performing/managing repair -->
          <div v-if="report.status === 'in_progress'" class="technician-workflow-container mb-4">
            <!-- If ETA is not set yet: Notice to set ETA above -->
            <div v-if="!getEtaText()" class="p-3 mb-4 rounded border text-center" style="background: #eff6ff; border: 1px solid #bfdbfe !important; color: #1e40af; font-size: 0.95rem; font-weight: 600;">
              <CIcon name="cil-clock" class="mr-1 text-primary" />
              <span>{{ text('กรุณาเลือกและบันทึกระยะเวลาดำเนินการซ่อม (ETA) ในกรอบสีเทาด้านบนเพื่อเปิดกรอบบันทึกผลการปิดงาน', 'Please select and save estimated repair time in the box above to unlock work completion form.') }}</span>
            </div>

            <!-- Full Repair Form (shown only when ETA is set) -->
            <div v-else class="p-4 rounded border mb-4" style="background: #ffffff; border-color: #e2e8f0;">

              <CForm @submit.prevent="finishWork">
                <div class="row mb-4">
                  <!-- Left: Maintenance Photo Upload -->
                  <div class="col-lg-5 col-md-12 mb-3 mb-lg-0 d-flex flex-column">
                    <label class="font-weight-bold text-dark mb-2">
                      <CIcon name="cil-camera" class="mr-1 text-primary" />
                      {{ $t('ticketDetail.tech.photoAfterLabel') }} <span class="text-danger">*</span>
                    </label>
                    <ImageAttachment :preview="previewUrl || ''" :filename="selectedFile ? selectedFile.name : ''"
                      :disabled="isProcessing" :invalid="Boolean(uploadError)" :max-count="3" @change="onFileChange" @remove="removeSelectedFile" />
                    <div v-if="uploadError" class="text-danger small mt-1 font-weight-bold">{{ uploadError }}</div>
                  </div>

                  <!-- Right: Maintenance Details Textarea -->
                  <div class="col-lg-7 col-md-12 d-flex flex-column">
                    <label class="font-weight-bold text-dark mb-2">
                      <CIcon name="cil-pencil" class="mr-1 text-primary" />
                      {{ $t('ticketDetail.tech.fixDetailLabel') }} <span class="text-danger">*</span>
                    </label>
                    <CTextarea
                      v-model="maintenanceDetails"
                      rows="7"
                      class="flex-grow-1"
                      :placeholder="$t('ticketDetail.tech.fixDetailPlaceholder')"
                      required
                    />
                  </div>
                </div>

                <!-- Spare parts section -->
                <div class="p-3 border rounded mb-4" style="background: #f8fafc;">
                  <label class="font-weight-bold text-dark mb-2">{{ $t('ticketDetail.tech.partsHeading') }} <span class="text-danger">*</span></label>
                  <div class="custom-control custom-checkbox mb-3">
                    <input id="admNoPartsUsed" v-model="noPartsUsed" type="checkbox" class="custom-control-input">
                    <label class="custom-control-label" for="admNoPartsUsed">{{ $t('ticketDetail.tech.noPartsUsed') }}</label>
                  </div>

                  <div v-if="!noPartsUsed" class="mt-3">
                    <div v-for="(part, index) in parts" :key="index" class="form-row align-items-end mb-2">
                      <div class="col-md-4 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partNameLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model="part.name" class="mb-0" :placeholder="$t('ticketDetail.tech.partNamePlaceholder')" />
                      </div>
                      <div class="col-md-2 col-6 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partQtyLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model.number="part.quantity" type="number" min="1" step="1" class="mb-0" />
                      </div>
                      <div class="col-md-3 col-6 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1">{{ $t('ticketDetail.tech.partPriceLabel') }} <span class="text-danger">*</span></label>
                        <CInput v-model.number="part.unitPrice" type="number" min="0" step="0.01" class="mb-0" />
                      </div>
                      <div class="col-md-2 col-8 mb-2 mb-md-0">
                        <label class="small font-weight-bold mb-1 text-muted">{{ $t('ticketDetail.tech.partTotalLabel') }}</label>
                        <div class="form-control bg-light text-right font-weight-bold d-flex align-items-center justify-content-end text-dark">
                          {{ partTotal(part).toLocaleString() }}
                        </div>
                      </div>
                      <div class="col-md-1 col-4 mb-2 mb-md-0">
                        <CButton
                          color="outline-danger"
                          class="w-100 d-flex align-items-center justify-content-center"
                          :disabled="parts.length === 1"
                          @click="removePart(index)"
                          :title="$t('ticketDetail.tech.removePartTitle')"
                        >
                          <CIcon name="cil-trash" />
                        </CButton>
                      </div>
                    </div>
                    <CButton color="outline-primary" size="sm" class="mt-2" @click="addPart">
                      {{ $t('ticketDetail.tech.addPartBtn') }}
                    </CButton>
                  </div>
                </div>

                <div class="text-center mt-3">
                  <CButton
                    type="submit"
                    size="lg"
                    class="px-5 shadow-sm text-white font-weight-bold"
                    style="background-color: #1e40af; border-color: #1e40af;"
                    :disabled="isProcessing"
                  >
                    <span v-if="!isProcessing">{{ $t('ticketDetail.tech.confirmClose') }}</span>
                    <span v-else>{{ $t('ticketDetail.tech.uploading') }}</span>
                  </CButton>
                </div>
              </CForm>
            </div>
          </div>
        </div>

        <!-- Step 4: ซ่อมเสร็จ รอประเมิน -->
        <div v-show="(activeStep === 'done' || activeStep === 'all') && isStepReached('done')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">4</span>
              {{ text('ผลการซ่อมแซม', 'Repair completion') }}
            </h3>
            <span class="text-muted small" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
              {{ formatDate(report.technicianPart.dateTimeFinished) }}
            </span>
          </div>

          <div class="mb-4">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('บันทึกผลการซ่อมของช่าง', 'Technician repair notes') }}</h5>
            <p class="sa-description">{{ getMaintenanceDetails() || text('ยังไม่มีการบันทึกผลการซ่อม', 'No repair result recorded yet') }}</p>
          </div>

          <div class="mb-4">
            <h5 class="font-weight-bold text-dark mb-2" style="font-size: 15px;">{{ text('ภาพถ่ายหลังซ่อมเสร็จ', 'After repair photo') }}</h5>
            <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair @open="openImage" @error="handleImageError" />
            <p v-else class="text-muted small">{{ text('ยังไม่มีภาพผลการซ่อม', 'No repair result image yet') }}</p>
          </div>

          <!-- Spare parts summary table -->
          <div class="spare-parts-section my-4">
            <h5 class="font-weight-bold text-dark mb-3">{{ $t('ticketDetail.parts.title') }}</h5>
            <div v-if="hasParts()" class="table-responsive border rounded bg-white">
              <table class="table table-sm table-hover mb-0" style="min-width: 540px;">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 60px; white-space: nowrap;" class="text-center">{{ $t('ticketDetail.parts.itemNo') }}</th>
                    <th style="white-space: nowrap;">{{ $t('ticketDetail.parts.name') }}</th>
                    <th style="width: 100px; white-space: nowrap;" class="text-center">{{ $t('ticketDetail.parts.qty') }}</th>
                    <th style="width: 140px; white-space: nowrap;" class="text-right">{{ $t('ticketDetail.parts.unitPrice') }}</th>
                    <th style="width: 140px; white-space: nowrap;" class="text-right">{{ $t('ticketDetail.parts.lineTotal') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(part, idx) in getTicketParts()" :key="idx">
                    <td class="text-center text-muted font-weight-bold" style="white-space: nowrap;">{{ idx + 1 }}</td>
                    <td class="font-weight-bold text-dark">{{ part.name }}</td>
                    <td class="text-center font-weight-bold" style="white-space: nowrap;">{{ part.quantity.toLocaleString() }}</td>
                    <td class="text-right" style="white-space: nowrap;">{{ part.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                    <td class="text-right font-weight-bold text-primary" style="white-space: nowrap;">{{ (part.quantity * part.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="bg-light font-weight-bold">
                    <td colspan="4" class="text-right text-dark" style="white-space: nowrap; font-size: 0.95rem;">{{ $t('ticketDetail.parts.totalCost') }}:</td>
                    <td class="text-right text-primary" style="white-space: nowrap; font-size: 1.05rem;">
                      {{ getPartsTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div v-else class="p-3 rounded bg-light border text-muted">
              <CIcon name="cil-check-circle" class="mr-1 text-success" />
              <span>{{ isNoPartsUsed() ? ($t('ticketDetail.parts.noParts') || text('ไม่มีการใช้อะไหล่', 'No parts used')) : text('ไม่มีการบันทึกรายการอะไหล่', 'No parts recorded') }}</span>
            </div>
          </div>

          <!-- If admin is reporter and status is done -->
          <div v-if="amIReporter && report.status === 'done'" class="p-4 my-4 rounded border text-center" style="background: #f8fafc;">
            <h5 class="font-weight-bold text-dark mb-2">{{ $t('ticketDetail.evalPlease') }}</h5>
            <div class="star-rating mb-3" role="radiogroup" :aria-label="$t('ticketDetail.evalPlease')">
              <span
                v-for="star in 5"
                :key="star"
                class="star" v-keyboard-activate role="radio" :aria-checked="String(rating === star)" :tabindex="star === (rating || 1) ? 0 : -1" :aria-label="text(star + ' จาก 5 ดาว', star + ' out of 5 stars')"
                :class="{ filled: star <= (hoverRating || rating) }"
                @mouseover="onStarHover(star)"
                @mouseleave="onStarLeave()"
                @click="onStarClick(star)"
                style="font-size: 32px; cursor: pointer; color: #cbd5e1; margin: 0 4px;"
              >★</span>
            </div>
            <CButton color="primary" size="lg" @click="submitEvaluation" :disabled="isSubmittingRating" class="px-5 font-weight-bold" style="background-color: #1e40af; border-color: #1e40af;">
              {{ isSubmittingRating ? $t('ticketDetail.saving') : $t('ticketDetail.evalSubmit') }}
            </CButton>
          </div>
        </div>

        <!-- Step 5: ประเมินแล้ว -->
        <div v-show="(activeStep === 'welldone' || activeStep === 'all') && isStepReached('welldone')" class="sa-step-box">
          <div class="sa-step-box-header">
            <h3>
              <span class="badge badge-primary mr-1">5</span>
              {{ text('ผลการประเมินความพึงพอใจ', 'Service evaluation') }}
            </h3>
            <span class="text-muted small" v-if="report.rating && report.rating.evaluatedAt">
              {{ formatDate(report.rating.evaluatedAt) }}
            </span>
          </div>

          <div v-if="report.rating && report.rating.stars">
            <!-- 1. Star Rating Banner -->
            <div class="text-center p-3 rounded bg-light border mb-4">
              <div class="sa-rating text-warning font-weight-bold mb-1" style="font-size: 1.8rem;">
                ★ {{ report.rating.stars }} / 5
              </div>
              <p class="text-muted mb-0 small">
                {{ text('ประเมินเมื่อ', 'Rated at') }} {{ formatDate(report.rating.evaluatedAt) }}
              </p>
              <p v-if="report.rating.comment || report.rating.feedback" class="mt-2 text-dark font-italic mb-0">
                "{{ report.rating.comment || report.rating.feedback }}"
              </p>
            </div>

            <!-- 2. Side-by-side Image Comparison (รูปภาพก่อนซ่อม vs รูปภาพหลังซ่อม) -->
            <div class="mb-4">
              <h5 class="font-weight-bold text-dark mb-3" style="font-size: 1rem;">
                <CIcon name="cil-image" class="mr-1 text-primary" />
                {{ text('เปรียบเทียบรูปภาพก่อน-หลังซ่อม', 'Before & After Repair Photos') }}
              </h5>
              <div class="row">
                <!-- Left: Before Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพก่อนซ่อม', 'Before Repair Photo') }}</span>
                    </div>
                    <ProblemPhotoGallery v-if="getProblemImages().length" v-model="activeBeforePhotoIdx" :images="getProblemImages()" :image-url="getImageUrl" compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพก่อนซ่อม', 'No before-repair photo') }}
                    </div>
                    <div v-if="getReportInformedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-clock" size="sm" class="mr-1 text-primary" />
                      <span>{{ text('เวลาที่แจ้ง:', 'Reported:') }} {{ formatDate(getReportInformedDate()) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Right: After Repair Photo -->
                <div class="col-md-6 mb-3">
                  <div class="border rounded bg-white p-3 text-center h-100 shadow-sm">
                    <div class="font-weight-bold text-dark mb-2 border-bottom pb-2">
                      <span>{{ text('รูปภาพหลังซ่อม', 'After Repair Photo') }}</span>

                    </div>
                    <ProblemPhotoGallery v-if="getMaintenanceImages().length" v-model="activeAfterPhotoIdx" :images="getMaintenanceImages()" :image-url="getImageUrl" after-repair compact @open="openImage" @error="handleImageError" />
                    <div v-else class="text-muted py-4 small">
                      <CIcon name="cil-image-broken" class="mb-1 text-secondary" /><br />
                      {{ text('ไม่มีรูปภาพหลังซ่อม', 'No after-repair photo') }}
                    </div>
                    <div v-if="getWorkFinishedDate()" class="small text-muted mt-2 border-top pt-2 d-flex align-items-center justify-content-center">
                      <CIcon name="cil-check-circle" size="sm" class="mr-1 text-success" />
                      <span>{{ text('เวลาที่ซ่อมเสร็จ:', 'Completed:') }} {{ formatDate(getWorkFinishedDate()) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Details Grid: Technician Info & Used Spare Parts -->
            <div class="row">
              <!-- Technician Info -->
              <div class="col-md-6 mb-3">
                <div class="border rounded bg-white p-3 h-100 shadow-sm">
                  <div class="font-weight-bold text-dark border-bottom pb-2 mb-2">
                    <CIcon name="cil-user" class="mr-1 text-primary" />
                    {{ text('ข้อมูลช่างที่ซ่อม', 'Assigned Technician') }}
                  </div>
                  <div class="d-flex align-items-center pt-1">
                    <CIcon name="cil-wrench" class="mr-2 text-primary" size="lg" />
                    <div>
                      <strong class="text-dark d-block">
                        {{ getTechnicianName() || text('ยังไม่มอบหมายช่าง', 'Unassigned') }}
                      </strong>
                      <small class="text-muted" v-if="report.technicianPart && report.technicianPart.dateTimeFinished">
                        {{ text('ซ่อมเสร็จเมื่อ', 'Completed at') }}: {{ formatDate(report.technicianPart.dateTimeFinished) }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Used Spare Parts Info -->
              <div class="col-md-6 mb-3">
                <div class="border rounded bg-white p-3 h-100 shadow-sm">
                  <div class="font-weight-bold text-dark border-bottom pb-2 mb-2 d-flex justify-content-between align-items-center">
                    <span>
                      <CIcon name="cil-list" class="mr-1 text-primary" />
                      {{ text('ข้อมูลอะไหล่ที่ใช้', 'Spare Parts Used') }}
                    </span>
                  </div>
                  <div v-if="hasParts()" class="pt-1">
                    <ul class="list-group list-group-flush mb-3">
                      <li v-for="(part, idx) in getTicketParts()" :key="idx" class="list-group-item bg-transparent px-0 py-2 d-flex justify-content-between align-items-center border-bottom" style="font-size: 0.95rem;">
                        <span class="text-dark font-weight-bold">
                          <span class="text-primary mr-1">{{ idx + 1 }}.</span> {{ part.name }}
                        </span>
                        <span class="badge badge-primary px-3 py-2" style="font-size: 0.88rem; font-weight: 600; border-radius: 999px;">
                          {{ part.quantity.toLocaleString() }} x {{ part.unitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                        </span>
                      </li>
                    </ul>
                    <div class="text-right border-top pt-2 font-weight-bold text-primary" style="font-size: 1rem;">
                      {{ $t('ticketDetail.parts.totalCost') }}: {{ getPartsTotal().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} {{ $t('ticketDetail.parts.currency') }}
                    </div>
                  </div>
                  <div v-else class="text-muted py-2" style="font-size: 0.95rem;">
                    <CIcon name="cil-check-circle" class="mr-1 text-success" />
                    <span>{{ isNoPartsUsed() ? ($t('ticketDetail.parts.noParts') || text('ไม่มีการใช้อะไหล่', 'No parts used')) : text('ไม่มีการบันทึกรายการอะไหล่', 'No parts recorded') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-muted text-center py-3">
            {{ report.status === 'done' ? text('อยู่ระหว่างรอการประเมินความพึงพอใจ', 'Awaiting rating') : text('ยังไม่มีข้อมูลการประเมิน', 'No evaluation data yet') }}
          </div>
        </div>
      </div>
    </section>

    <!-- Modal for Image Preview -->
    <CModal :aria-label="$t('sendReport.imageLabel')" :show.sync="imageModal" centered size="lg">
      <img :src="selectedImageUrl" class="img-fluid w-100" />
      <template #footer>
        <CButton color="secondary" @click="imageModal = false">{{ $t('ticketDetail.close') || text('ปิด', 'Close') }}</CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import keyboardActivate from '@/directives/keyboardActivate';
import CModal from '@/components/common/AccessibleModal.vue';
import ProblemPhotoGallery from "@/components/common/ProblemPhotoGallery.vue";
import axios from "axios";
import Service from "@/service/api";
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import reportDetailMixin from "@/i18n/reportDetailMixin";
import TicketDetailSummary from "@/components/common/TicketDetailSummary.vue";
import ImageAttachment from "@/components/common/ImageAttachment.vue";
import { compressImageToMax1MB, isImageFile } from "@/utils/imageCompressor";
import {
  getTicketAssetCode,
  getTicketProblemDetails,
  getTicketProblemImage,
  getTicketMaintenanceImage,
  getTicketMaintenanceImages,
  getTicketMaintenanceDetails,
  isTicketOutdoor,
  getTicketLocationFull,
  getTicketAvailableTime,
  formatTicketDate,
  getTicketUserName,
  getTicketPersonalPhone,
  getTicketOfficePhone,
  getTicketReporterAffiliation,
  getTicketSectionName,
  getTicketJobTypeName,
  getTicketOnBehalfOf,
  getTicketTechnicianPhone,
} from "@/utils/ticketViewHelper";

export default {
  directives: { keyboardActivate },
  name: "AdminReportDetail",
  components: { CModal, ProblemPhotoGallery, TicketDetailSummary, vSelect, ImageAttachment },
  mixins: [reportDetailMixin],
  props: {
    report: { type: Object, required: true },
  },
  data: () => ({
    activeStep: 'assigned',
    imageModal: false,
    selectedImageUrl: null,
    rating: 5,
    hoverRating: 0,
    isSubmittingRating: false,
    technicians: [],
    selectedTechnicianIds: [null],
    allCategories: [],
    selectedForwardCategoryId: null,
    showForwardBox: false,
    isProcessing: false,
    isInlineAssigning: false,
    map: null,
    marker: null,
    etaOption: 'immediate',
    etaWaitingDays: '',
    isEditingEta: false,
    etaWaitingText: '',
    etaCustomText: '',
    currentRepairStep: 1,
    selectedFile: null,
    selectedFiles: [],
    activeAfterPhotoIdx: 0,
    previewUrl: null,
    uploadError: null,
    maintenanceDetails: '',
    noPartsUsed: false,
    parts: [{ name: '', quantity: 1, unitPrice: 0 }],
  }),
  beforeDestroy() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },
  computed: {
    ...mapGetters("auth", ["profile"]),
    lang() {
      return this.$store.state.locale.lang || 'th';
    },
    defaultDetailStep() {
      const status = this.report && this.report.status;
      if (status === 'pending') {
        return 'assigned';
      }
      return ['assigned', 'in_progress', 'done', 'welldone'].includes(status)
        ? status : 'assigned';
    },
    overdue() {
      if (this.isPostponedActive) return false;
      const isOpen = ['pending', 'assigned', 'in_progress'].includes(this.report?.status);
      return isOpen && Boolean(this.report?.slaDeadline) && new Date(this.report.slaDeadline).getTime() < Date.now();
    },
    isEtaSaved() {
      const code = this.report?.estimatedRepairTime?.optionCode;
      return !!code && code !== 'none';
    },
    nextStep() {
      const next = {
        pending: ['รอหน่วยงานรับเรื่องและมอบหมายช่าง', 'Awaiting assignment by the maintenance section'],
        assigned: ['รอช่างรับงานและเริ่มซ่อม', 'Awaiting technician acceptance and repair'],
        in_progress: ['ช่างกำลังดำเนินการ รอบันทึกผลการซ่อม', 'Repair is in progress; awaiting completion'],
        done: ['ช่างแจ้งซ่อมเสร็จแล้ว รอประเมินบริการ', 'Technician reported completion; awaiting rating'],
        welldone: ['มีผลการซ่อมและการประเมินบริการแล้ว', 'Repair result and service rating are available'],
        cancelled: ['สิ้นสุดการดำเนินการสำหรับรายการนี้', 'This request is no longer active'],
      };
      return (next[this.report?.status] || ['', ''])[this.lang === 'en' ? 1 : 0];
    },
    timeline() {
      const statuses = ['pending', 'assigned', 'in_progress', 'done', 'welldone'];
      const dates = [
        this.getReportInformedDate(),
        this.getSupervisorInformedDate(),
        this.getWorkStartedDate(),
        this.getWorkFinishedDate(),
        this.report && this.report.rating && this.report.rating.evaluatedAt
      ];
      const currentIdx = statuses.indexOf(this.report && this.report.status);
      return statuses.map((status, index) => {
        let label = index === 0 ? this.text('แจ้งเรื่อง', 'Reported') : this.statusLabel(status);
        if (status === 'assigned') {
          label = this.text('มอบหมายช่าง', 'Assign Technician');
        }
        let reached = false;
        if (index === 0 || index === 1) {
          reached = true;
        } else if (this.isPostponedActive) {
          reached = false;
        } else {
          reached = Boolean(dates[index]) || (currentIdx !== -1 && index <= currentIdx);
        }
        return {
          status,
          label,
          date: (this.isPostponedActive && index >= 2) ? null : dates[index],
          reached
        };
      });
    },
    isValidAssignees() {
      const selectedIds = this.selectedTechnicianIds.filter(Boolean).map(String);
      return selectedIds.length > 0
        && selectedIds.length === this.selectedTechnicianIds.length
        && new Set(selectedIds).size === selectedIds.length;
    },
    availableTechnicians() {
      const ticketCat = this.report?.userPart?.category;
      const ticketSecId = ticketCat && typeof ticketCat === 'object' ? String(ticketCat._id || ticketCat.id || '') : String(ticketCat || '');
      const assignedIds = new Set(
        ((this.report?.supervisorPart?.assignments || []).map(a => a?.assignee && typeof a.assignee === 'object' ? String(a.assignee._id || a.assignee.id || '') : String(a?.assignee || ''))).filter(Boolean)
      );
      return (this.technicians || []).filter(item => {
        if (!item || !item._id) return false;
        if (assignedIds.has(String(item._id))) return true;
        if (!['technician', 'supervisor'].includes(item.role)) return false;
        if (item.isActive === false) return false;
        if (!ticketSecId) return true;
        const itemSec = item.section;
        const itemSecId = itemSec && typeof itemSec === 'object' ? String(itemSec._id || itemSec.id || '') : String(itemSec || '');
        return itemSecId === ticketSecId;
      });
    },
    amIReporter() {
      if (!this.report) return false;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const myId = String(this.profile?._id || this.profile?.id || userObj._id || userObj.id || '');
      if (!myId) return false;
      const reporterId = this.report.userPart?.user?._id
        ? String(this.report.userPart.user._id)
        : this.report.userPart?.user
          ? String(this.report.userPart.user)
          : null;
      return myId === reporterId;
    },
    mapPosition() {
      const loc = this.report?.userPart?.outdoorLocation;
      if (loc && loc.latitude && loc.longitude) {
        return {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        };
      }
      return { lat: 0, lng: 0 };
    },
  },
  created() {
    this.initEta();
    if (this.report?.status === "welldone" && this.report?.rating) {
      this.rating = this.report.rating.stars;
    }
    this.fetchTechnicians();
    this.fetchAllCategories();
    if (this.report?.status === 'pending') {
      this.startInlineAssign();
    }
  },
  methods: {
    text(th, en) {
      return this.lang === 'en' ? en : th;
    },
    statusLabel(status) {
      const map = {
        pending: ['รอรับเรื่อง', 'Pending'],
        assigned: ['มอบหมายช่าง', 'Assign Technician'],
        in_progress: ['กำลังซ่อม', 'In progress'],
        done: ['ซ่อมเสร็จ รอประเมิน', 'Repaired · awaiting rating'],
        welldone: ['ประเมินแล้ว', 'Rated'],
        cancelled: ['ยกเลิก', 'Cancelled'],
      };
      return (map[status] || [status, status])[this.lang === 'en' ? 1 : 0];
    },
    initEta() {
      if (this.report?.estimatedRepairTime) {
        this.etaOption = this.report.estimatedRepairTime.optionCode || 'none';
        const custom = this.report.estimatedRepairTime.customText || '';
        if (this.etaOption === 'waiting_parts') {
          this.etaWaitingText = custom;
        } else if (this.etaOption === 'custom') {
          this.etaCustomText = custom;
        }
      }
      if (this.isEtaSaved) {
        this.currentRepairStep = 2;
      } else {
        this.currentRepairStep = 1;
      }
    },
    startEditEta() {
      const eta = this.report && this.report.estimatedRepairTime;
      if (eta) {
        this.etaOption = eta.optionCode || 'none';
        this.etaWaitingText = eta.customText || '';
      } else {
        this.etaOption = 'none';
        this.etaWaitingText = '';
      }
      this.isEditingEta = true;
    },
    async saveEta() {
      if (!this.etaOption || this.etaOption === 'none') return;
      let customText = '';
      if (this.etaOption === 'waiting_parts') {
        if (!this.etaWaitingText || !String(this.etaWaitingText).trim()) {
          Swal.fire({
            icon: 'warning',
            title: this.text('กรุณาระบุรายละเอียด', 'Please specify details'),
            text: this.text('กรุณาพิมพ์รายละเอียดการรออะไหล่', 'Please enter spare parts waiting details'),
            confirmButtonColor: '#f9b115',
          });
          return;
        }
        customText = String(this.etaWaitingText).trim();
      } else if (this.etaOption === 'custom') {
        if (!this.etaCustomText || !this.etaCustomText.trim()) {
          Swal.fire({
            icon: 'warning',
            title: this.$t('ticketDetail.tech.etaCustomEmptyTitle'),
            text: this.$t('ticketDetail.tech.etaCustomEmptyText'),
            confirmButtonColor: '#f9b115',
          });
          return;
        }
        customText = this.etaCustomText.trim();
      }

      if (this.etaOption === 'waiting_parts') {
        const parsedDays = this.parseWaitingDays(customText);
        const targetDate = this.getNextWaitingPartsTargetDate(customText);
        const formattedTarget = targetDate ? this.formatDate(targetDate) : '';
        const informDate = this.getReportInformedDate();
        const informTimeStr = informDate ? new Date(informDate).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '';
        const daysInfoTh = parsedDays > 0 ? `กำหนดเวลาซ่อม (SLA) จะถูกขยายเวลาเพิ่มอีก <strong class="text-primary">${parsedDays} วัน</strong><br>เป็นสิ้นสุดวันที่` : 'กำหนดเวลาซ่อม (SLA) จะถูกขยายเวลาเป็นสิ้นสุดวันที่';
        const daysInfoEn = parsedDays > 0 ? `SLA deadline will be extended by <strong class="text-primary">${parsedDays} day(s)</strong><br>to` : 'SLA deadline will be extended to';
        const confirmRes = await Swal.fire({
          icon: 'question',
          title: this.text('ยืนยันการรออะไหล่', 'Confirm Waiting for Parts'),
          html: this.text(
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ${daysInfoTh} <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(ตามเวลาที่แจ้งซ่อมเดิม ${informTimeStr} น.)</small>
              </p>
              <p class="text-muted small mb-3">
                ระบบจะขยายกำหนดส่งมอบงานออกไปตามระยะเวลารออะไหล่
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (รออะไหล่: ${customText})
              </span>
            </div>`,
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ${daysInfoEn} <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(at original report time ${informTimeStr})</small>
              </p>
              <p class="text-muted small mb-3">
                SLA deadline will be extended during the waiting period.
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (Waiting for parts: ${customText})
              </span>
            </div>`
          ),
          showCancelButton: true,
          confirmButtonText: this.text('ยืนยันรออะไหล่', 'Confirm Waiting for Parts'),
          cancelButtonText: this.$t('mgmt.cancel'),
          confirmButtonColor: '#1e40af',
        });
        if (!confirmRes.isConfirmed) return;
      }

      if (this.etaOption === 'postponed') {
        const nextCount = (this.postponeCount || 0) + 1;
        const targetDate = this.getNextPostponeTargetDate();
        const formattedTarget = targetDate ? this.formatDate(targetDate) : '';
        const informDate = this.getReportInformedDate();
        const informTimeStr = informDate ? new Date(informDate).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : '';
        const confirmRes = await Swal.fire({
          icon: 'question',
          title: this.text('ยืนยันการเลื่อนซ่อม', 'Confirm Postpone Repair'),
          html: this.text(
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                ticket จะถูกเลื่อนไปเป็นวันที่ <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(ตามเวลาที่แจ้งซ่อมเดิม ${informTimeStr} น.)</small>
              </p>
              <p class="text-muted small mb-3">
                และจะเริ่มนับเวลา SLA ใหม่อีกครั้งเมื่อถึงวันและเวลาดังกล่าว
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (เลื่อนซ่อมครั้งที่ ${nextCount}/2)
              </span>
            </div>`,
            `<div class="text-center">
              <p class="mb-2" style="font-size: 1.05rem; line-height: 1.5;">
                Ticket will be postponed to <strong class="text-primary" style="font-size: 1.15rem;">${formattedTarget}</strong><br>
                <small class="text-muted">(at original report time ${informTimeStr})</small>
              </p>
              <p class="text-muted small mb-3">
                SLA countdown will restart at that time.
              </p>
              <span class="badge badge-primary px-3 py-2" style="font-size: 0.95rem; font-weight: 600; background-color: #1e40af;">
                (Postpone ${nextCount} of 2)
              </span>
            </div>`
          ),
          showCancelButton: true,
          confirmButtonText: this.text('ยืนยันเลื่อนซ่อม', 'Confirm Postpone'),
          cancelButtonText: this.$t('mgmt.cancel'),
          confirmButtonColor: '#1e40af',
        });
        if (!confirmRes.isConfirmed) return;
      }

      this.isProcessing = true;
      try {
        const res = await Service.ticket('patch', {
          optionCode: this.etaOption,
          customText: customText
        }, {
          urlAppend: `/${this.report._id}/estimated-time`
        });

        if (res.data && res.data.success) {
          const updated = res.data.data;
          await Swal.fire({
            icon: 'success',
            title: this.etaOption === 'postponed' ? this.text('เลื่อนซ่อมสำเร็จ', 'Repair postponed successfully') : this.$t('ticketDetail.tech.etaSaveOk'),
            timer: 1500,
            showConfirmButton: false,
          });
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          if (this.etaOption === 'postponed') {
            this.activeStep = 'assigned';
          }
          this.currentRepairStep = 2;
          this.isEditingEta = false;
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
        } else {
          throw new Error(res.data?.message || this.$t('ticketDetail.tech.etaSaveFail'));
        }
      } catch (error) {
        console.error('Save ETA error:', error);
        const errMsg = error.response?.data?.message || this.$t('ticketDetail.tech.etaSaveFail');
        Swal.fire({
          icon: 'error',
          title: this.$t('ticketDetail.swalError'),
          text: errMsg,
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async simulatePostponeReached() {
      try {
        const res = await Service.ticket('patch', {}, {
          urlAppend: `/${this.report._id}/simulate-postpone-reached`
        });
        if (res.data && res.data.success && res.data.data) {
          const updated = res.data.data;
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          this.activeStep = this.defaultDetailStep;
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
          Swal.fire({
            icon: 'info',
            title: this.text('จำลองเวลาสำเร็จ', 'Simulated successfully'),
            text: this.text('ถึงเวลาตามที่เลื่อนซ่อมแล้ว! Timeline ขั้นตอนต่อไปเปิดให้ดำเนินการ และเริ่มนับ SLA ใหม่แล้ว', 'Postpone time reached! Next timeline step is now open and SLA is actively counting.'),
            timer: 2500,
            showConfirmButton: true
          });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    },
    async resetPostpone() {
      try {
        const res = await Service.ticket('patch', {}, {
          urlAppend: `/${this.report._id}/reset-postpone`
        });
        if (res.data && res.data.success && res.data.data) {
          const updated = res.data.data;
          if (this.report && updated) {
            Object.keys(updated).forEach(k => {
              this.$set(this.report, k, updated[k]);
            });
          }
          this.initEta();
          this.$emit('update:report', { ...this.report, ...updated });
          this.$emit('refresh', { keepOpen: true, updatedTicket: updated });
          Swal.fire({
            icon: 'success',
            title: this.text('รีเซ็ตสำเร็จ', 'Reset successfully'),
            text: this.text('รีเซ็ตการเลื่อนซ่อมเรียบร้อย สามารถทดสอบใหม่ได้', 'Postpone reset complete. You can test again.'),
            timer: 1500,
            showConfirmButton: false
          });
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message });
      }
    },
    async onFileChange(eventOrFile) {
      let files = [];
      if (eventOrFile && eventOrFile.files && Array.isArray(eventOrFile.files)) {
        files = eventOrFile.files;
      } else if (eventOrFile && eventOrFile.target && eventOrFile.target.files) {
        files = Array.from(eventOrFile.target.files);
      } else if (Array.isArray(eventOrFile)) {
        files = eventOrFile;
      } else if (eventOrFile instanceof File || (eventOrFile && eventOrFile.name)) {
        files = [eventOrFile];
      }
      this.selectedFiles = files;
      this.selectedFile = files[0] || null;
      this.uploadError = null;

      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
      if (this.selectedFile) {
        try {
          this.previewUrl = URL.createObjectURL(this.selectedFile);
        } catch (e) {
          this.previewUrl = null;
        }
      } else {
        this.previewUrl = null;
      }
    },
    removeSelectedFile(idx) {
      if (typeof idx === 'number' && this.selectedFiles.length > 0) {
        this.selectedFiles.splice(idx, 1);
        this.selectedFile = this.selectedFiles[0] || null;
      } else {
        if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
        this.selectedFile = null;
        this.selectedFiles = [];
        this.previewUrl = null;
      }
      this.uploadError = null;
    },
    addPart() {
      this.parts.push({ name: "", quantity: 1, unitPrice: 0 });
    },
    removePart(index) {
      if (this.parts.length > 1) {
        this.parts.splice(index, 1);
      }
    },
    partTotal(part) {
      return (Number(part.quantity) || 0) * (Number(part.unitPrice) || 0);
    },
    async finishWork() {
      const filesToUpload = (this.selectedFiles && this.selectedFiles.length > 0)
        ? this.selectedFiles
        : (this.selectedFile ? [this.selectedFile] : []);

      if (filesToUpload.length === 0) {
        this.uploadError = this.text("กรุณาแนบภาพถ่ายหลังซ่อมเสร็จ", "Please attach photo after repair");
        Swal.fire({
          icon: "warning",
          title: this.$t("ticketDetail.swalWarn"),
          text: this.uploadError,
        });
        return;
      }
      if (!this.maintenanceDetails || !this.maintenanceDetails.trim()) {
        Swal.fire({
          icon: "warning",
          title: this.$t("ticketDetail.swalWarn"),
          text: this.text("กรุณาระบุรายละเอียดการซ่อม", "Please enter repair notes"),
        });
        return;
      }

      let formattedParts = [];
      if (!this.noPartsUsed) {
        const validParts = this.parts.filter(
          (p) => p && String(p.name || "").trim() !== ""
        );
        if (validParts.length === 0) {
          Swal.fire({
            icon: "warning",
            title: this.$t("ticketDetail.swalWarn"),
            text: this.text("กรุณากรอกรายการอะไหล่อย่างน้อย 1 รายการ หรือเลือก ไม่มีการใช้อะไหล่", "Please add at least one spare part or check No parts used"),
          });
          return;
        }
        formattedParts = validParts.map((p) => ({
          name: String(p.name).trim(),
          quantity: Math.max(1, Number(p.quantity) || 1),
          unitPrice: Math.max(0, Number(p.unitPrice) || 0),
        }));
      }

      const confirm = await Swal.fire({
        icon: "question",
        title: this.text("ยืนยันส่งงานซ่อม", "Confirm repair finish"),
        text: this.text("ต้องการบันทึกและปิดงานซ่อมนี้ใช่หรือไม่?", "Do you want to save repair results and close this ticket?"),
        showCancelButton: true,
        confirmButtonText: this.text("ยืนยันส่งงาน", "Confirm finish"),
        cancelButtonText: this.$t("mgmt.cancel"),
        confirmButtonColor: "#1e40af",
      });

      if (!confirm.isConfirmed) return;

      this.isProcessing = true;
      try {
        const formData = new FormData();
        filesToUpload.slice(0, 3).forEach((file, index) => {
          const fileName = file.name || `maintenance_${index + 1}.jpg`;
          formData.append("maintenanceImages", file, fileName);
        });
        const primaryFile = filesToUpload[0];
        formData.append("maintenanceImage", primaryFile, primaryFile.name || "maintenance.jpg");
        if (primaryFile && primaryFile.watermarkInfo) {
          formData.append("photoTimestamp", primaryFile.watermarkInfo.photoTimestamp || new Date().toISOString());
          formData.append("photoSource", primaryFile.watermarkInfo.photoSource || 'camera');
        }
        formData.append("maintenanceDetails", this.maintenanceDetails.trim());
        formData.append("noPartsUsed", String(this.noPartsUsed));
        formData.append("parts", JSON.stringify(formattedParts));

        const res = await Service.ticket("put", formData, {
          urlAppend: `/${this.report._id}/finish`,
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (res.data && res.data.success) {
          await Swal.fire({
            icon: "success",
            title: this.text("ส่งงานซ่อมเรียบร้อย", "Repair submitted successfully"),
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit("refresh");
          this.$emit("close");
        } else {
          throw new Error(res.data?.message || "Failed to finish ticket");
        }
      } catch (err) {
        console.error("Finish ticket error:", err);
        Swal.fire({
          icon: "error",
          title: this.$t("ticketDetail.swalError"),
          text: err.response?.data?.message || err.message,
        });
      } finally {
        this.isProcessing = false;
      }
    },
    filterTechnicianOption(option, label, search) {
      const query = (search || "").toLowerCase().trim();
      if (!query) return true;
      const text = this.technicianOptionLabel(option).toLowerCase();
      return text.includes(query);
    },
    technicianOptionLabel(tech) {
      if (!tech) return '-';
      const fullName = `${tech.name || ''} ${tech.surname || ''}`.trim();
      const departmentName = tech.department?.name || tech.section?.departmentName || '-';
      const jobType = tech.jobType || '-';
      return `${fullName} • ${departmentName} • ${jobType}`;
    },
    addAssignee() {
      if (this.selectedTechnicianIds.length < 3) {
        this.selectedTechnicianIds.push(null);
      }
    },
    removeAssignee(index) {
      if (this.selectedTechnicianIds.length > 1) {
        this.selectedTechnicianIds.splice(index, 1);
      }
    },
    async fetchTechnicians() {
      try {
        const url = `${process.env.VUE_APP_APP_URL}/user/technicians?includeSupervisors=true&includeAvailability=true`;
        const response = await axios.get(url);
        if (response.data && response.data.success) {
          this.technicians = Array.isArray(response.data.data)
            ? response.data.data.filter(Boolean)
            : [];
        }
      } catch (error) {
        this.technicians = this.technicians.map(tech => {
          const { activeTicketCount, ...identity } = tech;
          return identity;
        });
        console.error("Error fetching technicians:", error);
      }
    },
    startInlineAssign() {
      if (this.technicians.length === 0) {
        this.fetchTechnicians();
      }
      this.showForwardBox = false;

      if (this.report && this.report.supervisorPart && this.report.supervisorPart.assignments) {
        const activeAssignments = this.report.supervisorPart.assignments.filter(a => a.status !== 'rejected');
        if (activeAssignments.length > 0) {
          this.selectedTechnicianIds = activeAssignments.map(a =>
            a.assignee?._id ? String(a.assignee._id) : String(a.assignee)
          );
        } else {
          this.selectedTechnicianIds = [null];
        }
      } else {
        this.selectedTechnicianIds = [null];
      }

      this.isInlineAssigning = true;
    },
    cancelInlineAssign() {
      this.isInlineAssigning = false;
      this.selectedTechnicianIds = [null];
    },
    async fetchAllCategories() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_APP_URL}/category`);
        if (res.data && res.data.success) {
          this.allCategories = Array.isArray(res.data.data)
            ? res.data.data.filter(Boolean)
            : [];
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    },
    isCurrentCategory(catId) {
      if (!this.report.userPart || !this.report.userPart.category) return false;
      const currentCat = this.report.userPart.category;
      const currentId = typeof currentCat === 'object' ? currentCat._id : currentCat;
      return String(catId) === String(currentId);
    },
    async assignTask() {
      if (!this.isValidAssignees) return;

      const selectedTechnicians = this.technicians.filter(tech => tech && this.selectedTechnicianIds.includes(tech._id));
      const techName = selectedTechnicians.length
        ? selectedTechnicians.map(tech => `${tech.name || ''} ${tech.surname || ''}`.trim()).join(', ')
        : this.$t('ticketDetail.selectedTechFallback');
      const isChanging = this.report.status !== 'pending';
      const result = await Swal.fire({
        title: isChanging ? this.$t('ticketDetail.confirmChangeTechTitle') : this.$t('ticketDetail.confirmAssignTitle'),
        html: isChanging
          ? this.$t('ticketDetail.confirmChangeTechHtml').replace('{name}', techName)
          : this.$t('ticketDetail.confirmAssignTechHtml').replace('{name}', techName),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        confirmButtonText: this.$t('mgmt.confirm'),
        cancelButtonText: this.$t('mgmt.cancel')
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          const response = await Service.ticket('patch', {
            assigneeIds: this.selectedTechnicianIds,
            expectedVersion: this.report.__v || 0
          }, { urlAppend: `/${this.report._id}/assignments` });

          if (response.data.success) {
            Swal.fire(this.$t('mgmt.success'), isChanging ? this.$t('ticketDetail.changeTechOk') : this.$t('ticketDetail.assignTechOk'), 'success');
            this.isInlineAssigning = false;
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error(response.data.message || 'Failed to update ticket');
          }
        } catch (error) {
          console.error(error);
          let errorMessage = error.response?.data?.message
            || (error.request ? this.$t('ticketDetail.assignmentNetworkError') : error.message)
            || this.$t('ticketDetail.assignTechFail');
          if (errorMessage && errorMessage.includes('Supervisor self-assignment must put the requesting Supervisor first')) {
            errorMessage = 'ไม่สามารถมอบงานให้ supervisor ได้';
          }
          Swal.fire(this.$t('ticketDetail.swalError'), errorMessage, 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    async forwardTicket() {
      if (!this.selectedForwardCategoryId) return;

      const result = await Swal.fire({
        title: this.$t('ticketDetail.supervisor.forwardConfirmTitle'),
        text: this.$t('ticketDetail.supervisor.forwardConfirmText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#dc2626',
        confirmButtonText: this.$t('mgmt.confirm'),
        cancelButtonText: this.$t('mgmt.cancel')
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          await Service.ticket('post', {
            newCategoryId: this.selectedForwardCategoryId
          }, { urlAppend: `/${this.report._id}/forward` });

          Swal.fire({ title: this.$t('mgmt.success'), text: this.$t('ticketDetail.supervisor.forwardOk'), icon: 'success' });
          this.showForwardBox = false;
          this.$emit('refresh');
          this.$emit('close');
        } catch (error) {
          console.error(error);
          Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.supervisor.forwardFail'), 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    isTicketReporter(report = this.report) {
      if (!report) return false;
      if (report._role === 'reporter') return true;
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : {};
      const userId = String(this.$store?.state?.auth?.profile?._id || this.$store?.state?.auth?.profile?.id || userObj._id || userObj.id || '');
      if (!userId) return false;
      const userPart = report.userPart || {};
      const rawUser = userPart.user;
      const reporterId = String(
        userPart.userId ||
        userPart.id ||
        userPart._id ||
        (typeof rawUser === 'object' && rawUser !== null ? (rawUser._id || rawUser.id) : rawUser) ||
        ''
      );
      return Boolean(reporterId && userId === reporterId);
    },
    canCancelTicket(report = this.report) {
      if (!report) return false;
      if (!this.isTicketReporter(report)) return false;
      if (['done', 'welldone', 'cancelled', 'cancel', 'in_progress'].includes(report.status)) {
        return false;
      }
      const assignments = report.supervisorPart?.assignments || [];
      const hasAcceptedAssignment = assignments.some(a => a.status === 'accepted');
      const hasAcceptedDate = Boolean(report.technicianPart?.dateTimeAccepted);
      if (hasAcceptedAssignment || hasAcceptedDate) {
        return false;
      }
      return ['pending', 'assigned'].includes(report.status);
    },
    async cancelTicket() {
      const result = await Swal.fire({
        title: this.$t('ticketDetail.cancelTicketTitle'),
        html: this.$t('ticketDetail.cancelTicketHtml'),
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: this.$t('ticketDetail.cancelPlaceholder'),
        inputAttributes: {
          'aria-label': this.$t('ticketDetail.cancelAria')
        },
        showCancelButton: true,
        confirmButtonColor: '#1e40af',
        cancelButtonColor: '#dc2626',
        confirmButtonText: this.$t('ticketDetail.cancelConfirmBtn'),
        cancelButtonText: this.$t('ticketDetail.closeBtn'),
        inputValidator: (value) => {
          if (!value || value.trim() === '') {
            return this.$t('ticketDetail.cancelReasonRequired');
          }
        }
      });

      if (result.isConfirmed) {
        this.isProcessing = true;
        try {
          const response = await Service.ticket('put', {
            userId: this.profile._id,
            cancelReason: result.value
          }, { urlAppend: `/${this.report._id}/cancel` });

          if (response.data.success) {
            Swal.fire(this.$t('mgmt.success'), this.$t('ticketDetail.cancelOk'), 'success');
            this.$emit('refresh');
            this.$emit('close');
          } else {
            throw new Error(response.data.message || 'Failed to cancel ticket');
          }
        } catch (error) {
          console.error('Cancel ticket error:', error);
          Swal.fire(this.$t('ticketDetail.swalError'), error.response?.data?.message || this.$t('ticketDetail.cancelFail'), 'error');
        } finally {
          this.isProcessing = false;
        }
      }
    },
    getAssigneeItemName(item) {
      if (!item || !item.assignee) return '-';
      if (typeof item.assignee === 'object' && item.assignee !== null) {
        const name = `${item.assignee.name || ''} ${item.assignee.surname || ''}`.trim();
        if (name) return name;
      }
      const assigneeId = String(item.assignee?._id || item.assignee);
      const myId = String(this.profile?._id || this.profile?.id || '');
      if (assigneeId && assigneeId === myId && this.profile) {
        const myName = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
        return myName || this.profile.username || this.$t('ticketDetail.tech.selfAdmin');
      }
      const found = this.technicians.find(t => String(t._id || t.id) === assigneeId);
      if (found) {
        const tName = `${found.name || ''} ${found.surname || ''}`.trim();
        return tName || found.username || '-';
      }
      return '-';
    },
    getTechnicianName() {
      const assignments = Array.isArray(this.report?.supervisorPart?.assignments)
        ? this.report.supervisorPart.assignments
        : [];
      if (assignments.length > 0) {
        const accepted = assignments.filter(a => a && a.status === 'accepted');
        const active = assignments.filter(a => a && a.status !== 'rejected');
        const listToUse = accepted.length > 0 ? accepted : (active.length > 0 ? active : assignments);

        const techList = this.technicians || this.techniciansList || [];
        const names = listToUse
          .map(a => {
            const assignee = a?.assignee;
            if (assignee && typeof assignee === 'object') {
              const n = `${assignee.name || assignee.firstname || ''} ${assignee.surname || assignee.lastname || ''}`.trim();
              if (n) return n;
              if (assignee.username) return assignee.username;
            }
            if (typeof assignee === 'string' || typeof assignee === 'number') {
              const strId = String(assignee);
              if (this.profile && String(this.profile._id || this.profile.id) === strId) {
                const n = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
                if (n) return n;
                if (this.profile.username) return this.profile.username;
              }
              if (Array.isArray(techList)) {
                const found = techList.find(t => String(t._id || t.id) === strId);
                if (found) {
                  const n = `${found.name || ''} ${found.surname || ''}`.trim();
                  if (n) return n;
                  if (found.username) return found.username;
                }
              }
            }
            return null;
          })
          .filter(Boolean);

        if (names.length > 0) {
          return names.join(', ');
        }
      }

      const tech = this.report?.supervisorPart?.assignedTechnician;
      if (tech) {
        if (typeof tech === 'object') {
          const n = `${tech.name || ''} ${tech.surname || ''}`.trim();
          if (n) return n;
          if (tech.username) return tech.username;
        } else if (typeof tech === 'string' || typeof tech === 'number') {
          const strId = String(tech);
          if (this.profile && String(this.profile._id || this.profile.id) === strId) {
            const n = `${this.profile.name || ''} ${this.profile.surname || ''}`.trim();
            if (n) return n;
          }
          const techList = this.technicians || this.techniciansList || [];
          if (Array.isArray(techList)) {
            const found = techList.find(t => String(t._id || t.id) === strId);
            if (found) {
              const n = `${found.name || ''} ${found.surname || ''}`.trim();
              if (n) return n;
            }
          }
        }
      }

      const tpTech = this.report?.technicianPart?.technician;
      if (tpTech && typeof tpTech === 'object') {
        const n = `${tpTech.name || ''} ${tpTech.surname || ''}`.trim();
        if (n) return n;
      }

      if (this.report?.status === 'assigned') {
        return this.$t ? this.$t('ticketDetail.techPending') : 'รอยืนยันการรับงาน';
      }

      return this.$t ? this.$t('ticketDetail.techNotYet') : 'ยังไม่มอบหมายช่าง';
    },
    async submitEvaluation() {
      if (!this.rating || this.rating < 1) return;
      this.isSubmittingRating = true;
      try {
        const res = await Service.ticket('post', { stars: this.rating }, {
          urlAppend: `/${this.report._id}/rating`
        });
        if (res.data.success || res.status === 200) {
          await Swal.fire({
            icon: 'success',
            title: this.$t('ticketDetail.swalEvalOk'),
            confirmButtonColor: '#1e40af',
            timer: 1600,
            showConfirmButton: false,
          });
          this.$emit('refresh');
          this.$emit('close');
        }
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: 'error', title: this.$t('ticketDetail.swalError'), text: this.$t('ticketDetail.swalSaveFail') });
      } finally {
        this.isSubmittingRating = false;
      }
    },
    onStarHover(star) {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = star;
      }
    },
    onStarLeave() {
      if (this.report && this.report.status === 'done') {
        this.hoverRating = 0;
      }
    },
    onStarClick(star) {
      if (this.report && this.report.status === 'done') {
        this.rating = star;
      }
    },
    openImage(img) {
      if (!img) return;
      this.selectedImageUrl = this.getImageUrl(img);
      this.imageModal = true;
    },
    handleImageError(e) { console.error("Image failed to load"); },
    getImageUrl(f) {
      if (!f) return "";
      const filename = f.includes("/") ? f.split("/").pop() : f;
      return `${process.env.VUE_APP_APP_URL}/uploads/${encodeURIComponent(filename)}`;
    },
    getUserName() {
      return getTicketUserName(this.report);
    },
    getOnBehalfOf() {
      return getTicketOnBehalfOf(this.report);
    },
    getPersonalPhone() { return getTicketPersonalPhone(this.report); },
    getOfficePhone() { return getTicketOfficePhone(this.report); },
    getTechnicianPhone() {
      return getTicketTechnicianPhone(this.report, this.technicians || this.techniciansList, this.profile);
    },
    getReporterAffiliation() { return getTicketReporterAffiliation(this.report); },
    getSectionName() { return getTicketSectionName(this.report, this.lang); },
    getCategoryName() { return getTicketJobTypeName(this.report, this.lang); },
    getLocationFull() {
      return getTicketLocationFull(this.report, this.$t.bind(this), this.$store.state.locale.lang);
    },
    getAvailableTime() {
      return getTicketAvailableTime(this.report, this.lang, this.$t.bind(this));
    },
    getAssetCode() { return getTicketAssetCode(this.report); },
    getProblemDetails() { return getTicketProblemDetails(this.report); },
    getProblemImage() { return getTicketProblemImage(this.report); },
    getMaintenanceImage() { return getTicketMaintenanceImage(this.report); },
    getMaintenanceImages() { return getTicketMaintenanceImages(this.report); },
    getActiveMaintenanceImage() {
      const images = this.getMaintenanceImages();
      if (!images || images.length === 0) return this.getMaintenanceImage();
      return images[this.activeAfterPhotoIdx] || images[0];
    },
    getMaintenanceDetails() { return getTicketMaintenanceDetails(this.report); },
    getTicketParts() {
      const technicianPart = this.report?.technicianPart || {};
      if (Array.isArray(technicianPart.parts) && technicianPart.parts.length > 0) {
        return technicianPart.parts
          .filter(part => part && String(part.name || '').trim() && String(part.name || '').trim() !== '-')
          .map(part => ({
            name: String(part.name).trim(),
            quantity: Math.max(1, Number(part.quantity) || 1),
            unitPrice: Math.max(0, Number(part.unitPrice) || 0),
          }));
      }
      if (technicianPart.noPartsUsed) return [];
      const legacyName = String(technicianPart.spareParts || '').trim();
      if (legacyName && legacyName !== '-') {
        return [{
          name: legacyName,
          quantity: Math.max(1, Number(technicianPart.quantity) || 1),
          unitPrice: Math.max(0, Number(technicianPart.cost) || 0),
        }];
      }
      return [];
    },
    hasParts() {
      return this.getTicketParts().length > 0;
    },
    isNoPartsUsed() {
      return this.report?.technicianPart?.noPartsUsed === true;
    },
    getPartsTotal() {
      return this.getTicketParts().reduce(
        (total, part) => total + (part.quantity * part.unitPrice),
        0
      );
    },
    formatDate(dateString) {
      return formatTicketDate(dateString, this.lang);
    },
    isOutdoor() {
      return isTicketOutdoor(this.report);
    },

  },
};
</script>

<style scoped>
.star.filled {
  color: #f59e0b !important;
}
</style>
